import React, { useState, useEffect } from "react";
import { ResponsiveStream } from "@nivo/stream";
import PropTypes from "prop-types";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  fetchClaraResponseCategorizationByDateForIframe
} from "../services/api";
import { 
  ENV_TYPE_PROD,
  CATEGORY_TYPE_CLARA_RESPONSE,
  CATEGORY_TYPE_USER_QUERY
} from "../constants";

/**
 * Custom hook to detect ultra-wide screens (aspect ratio > 2.2)
 * @returns {boolean} True if screen is ultra-wide
 */
const useIsUltraWide = () => {
  const [isUltraWide, setIsUltraWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth / window.innerHeight > 2.2 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsUltraWide(window.innerWidth / window.innerHeight > 2.2);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isUltraWide;
};

const ClaraFeedbackTrends = ({ id: propId, source: propSource, envType: propEnvType }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Check if this is an iframe route (no login required)
  const isIframeRoute = location.pathname.includes('/iframe/');
  
  // Get parameters from URL params, fallback to props (no default id - empty means all)
  const id = searchParams.get('id') || propId || null;
  const source = searchParams.get('source') || propSource || "invite-clara";
  const darkMode = searchParams.get('darkMode') === 'true';
  
  const envType = propEnvType ?? ENV_TYPE_PROD;
  
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryType, setCategoryType] = useState(CATEGORY_TYPE_USER_QUERY);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Detect if screen is ultra-wide for horizontal layout
  const isUltraWide = useIsUltraWide();

  useEffect(() => {
    const fetchData = async () => {
      if (!source) {
        setError("Missing required parameter: source is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchClaraResponseCategorizationByDateForIframe(id, source, envType, categoryType);

        if (response.success) {
          // Determine which field to use based on category type
          const categoryField = categoryType === CATEGORY_TYPE_USER_QUERY 
            ? 'user_query_category' 
            : 'clara_response_category';
          
          // Transform API response to stream chart data format
          // Group data by category and create series for each category
          const categoryMap = new Map();
          const dateSet = new Set();

          // First pass: collect all unique dates and categories
          response.data.forEach(item => {
            dateSet.add(item.creation_date);
            const categoryValue = item[categoryField];
            if (!categoryMap.has(categoryValue)) {
              categoryMap.set(categoryValue, new Map());
            }
            categoryMap.get(categoryValue).set(item.creation_date, item.count);
          });

          // Sort dates chronologically
          const sortedDates = Array.from(dateSet).sort();

          // Create data structure for stream chart
          // Each object represents a date point with all categories
          const streamData = sortedDates.map(date => {
            const dataPoint = {};
            categoryMap.forEach((dateMap, category) => {
              dataPoint[category] = dateMap.get(date) || 0;
            });
            return dataPoint;
          });

          // Create keys array (list of all categories)
          const keys = Array.from(categoryMap.keys());

          setData({ streamData, keys, dates: sortedDates });
        } else {
          throw new Error(response.error || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching Clara data:", err);
        
        if (err.response?.status === 403) {
          setError("Access forbidden. This iframe can only be embedded on authorized domains.");
        } else {
          setError(err.message || "Failed to fetch data");
        }
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [id, source, envType, isIframeRoute, categoryType]);

  // Show initial loading screen only on first load
  if (isInitialLoad && isLoading) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <span className={`text-xl font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Checking Authentication...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg shadow-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Error</h1>
          <p className={`text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Access Denied</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            You do not have the required permissions to view this content.
          </p>
        </div>
      </div>
    );
  }

  if (!data.streamData || data.streamData.length === 0) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg shadow-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Category Usage Over Time
          </h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No data available for the specified parameters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
      <div className="max-w-6xl mx-auto">
        <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={isUltraWide ? 'flex items-center gap-6' : ''}>
            {/* Title and Toggle Section */}
            {isUltraWide ? (
              <div className="flex-shrink-0 flex items-center justify-center" style={{ width: '120px', height: '600px' }}>
                <div style={{ 
                  transform: 'rotate(-90deg)', 
                  transformOrigin: 'center center',
                  width: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <h1 className={`text-3xl font-bold mb-4 text-center whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Clara Trends Over Time
                  </h1>
                  
                  {/* Category Type Toggle - Only show for iframe routes */}
                  {isIframeRoute && (
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex rounded-lg border p-1" style={{
                        backgroundColor: darkMode ? '#374151' : '#ffffff',
                        borderColor: darkMode ? '#4b5563' : '#d1d5db'
                      }}>
                        <button
                          onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                          disabled={isLoading}
                          className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_USER_QUERY
                              ? "bg-blue-600 text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          User Query Category
                        </button>
                        <button
                          onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                          disabled={isLoading}
                          className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                              ? "bg-blue-600 text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          Clara Response Category
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h1 className={`text-3xl font-bold mb-4 text-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Clara Trends Over Time
                </h1>
                
                {/* Category Type Toggle - Only show for iframe routes */}
                {isIframeRoute && (
                  <div className="flex justify-center mb-6">
                    <div className={`inline-flex rounded-lg border p-1 ${
                      darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                    }`}>
                      
                      <button
                        onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                        disabled={isLoading}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                          categoryType === CATEGORY_TYPE_USER_QUERY
                            ? "bg-blue-600 text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        User Query Category
                      </button>
                      <button
                        onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                        disabled={isLoading}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                          categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                            ? "bg-blue-600 text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Clara Response Category
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Chart Section */}
            <div className={`flex justify-center relative ${isUltraWide ? 'flex-1' : ''}`}>
            {/* Loading overlay - only show during category toggle */}
            {!isInitialLoad && isLoading && (
              <div className={`absolute inset-0 bg-opacity-75 flex items-center justify-center z-10 rounded-lg ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                <div className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Updating chart...</div>
              </div>
            )}
            <div style={{ height: "600px", width: "100%" }}>
              <ResponsiveStream
                key={`${id}-${source}-${categoryType}`}
                data={data.streamData}
                keys={data.keys}
                margin={{ top: 50, right: 150, bottom: 50, left: 60 }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: 'bottom',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: 'Date',
                  legendOffset: 46,
                  // Dynamic tick sampling to prevent congestion
                  tickValues: (() => {
                    const maxTicks = 12; // Maximum number of ticks to show
                    const totalDates = data.dates.length;
                    
                    if (totalDates <= maxTicks) {
                      // Show all dates if we have fewer than maxTicks
                      return data.dates.map((_, i) => i);
                    }
                    
                    // Calculate interval to show approximately maxTicks
                    const interval = Math.ceil(totalDates / maxTicks);
                    const tickIndices = [];
                    
                    for (let i = 0; i < totalDates; i += interval) {
                      tickIndices.push(i);
                    }
                    
                    // Always include the last date
                    if (tickIndices[tickIndices.length - 1] !== totalDates - 1) {
                      tickIndices.push(totalDates - 1);
                    }
                    
                    return tickIndices;
                  })(),
                  format: (value) => {
                    // Format date display
                    const dateIndex = parseInt(value);
                    return data.dates[dateIndex] || value;
                  }
                }}
                axisLeft={{
                  orient: 'left',
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Count',
                  legendOffset: -50
                }}
                offsetType="none"
                colors={{ scheme: 'nivo' }}
                fillOpacity={0.85}
                borderColor={{ theme: 'background' }}
                enableGridX={true}
                enableGridY={true}
                curve="catmullRom"
                theme={{
                  axis: {
                    ticks: {
                      text: {
                        fill: darkMode ? '#e5e7eb' : '#333333'
                      }
                    },
                    legend: {
                      text: {
                        fill: darkMode ? '#e5e7eb' : '#333333'
                      }
                    }
                  },
                  grid: {
                    line: {
                      stroke: darkMode ? '#374151' : '#e0e0e0'
                    }
                  },
                  tooltip: {
                    container: {
                      background: darkMode ? '#1f2937' : '#ffffff',
                      color: darkMode ? '#e5e7eb' : '#333333'
                    }
                  }
                }}
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    translateX: 100,
                    itemWidth: 80,
                    itemHeight: 20,
                    itemTextColor: darkMode ? '#e5e7eb' : '#333333',
                    symbolSize: 12,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemTextColor: darkMode ? '#ffffff' : '#000000'
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ layer }) => (
                  <div className={`p-4 rounded-xl shadow-xl border backdrop-blur-sm ${
                    darkMode 
                      ? 'bg-gray-800 border-gray-600' 
                      : 'bg-white border-gray-200'
                  }`}>
                    <div className={`font-bold text-lg mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{layer.id}</div>
                    <div className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Count: {layer.value}</div>
                  </div>
                )}
                animate={true}
                motionConfig="gentle"
              />
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ClaraFeedbackTrends.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  source: PropTypes.oneOf(["invite-clara", "wtw-clara"]),
  envType: PropTypes.oneOf([0, 1, 2]), // 0 = test, 1 = production, 2 = dev
};

export default ClaraFeedbackTrends;

